/**
 * dt-button - Button component with variants and states
 * Variants: primary, secondary, outline, ghost
 * States: disabled, loading
 */
import { BaseElement } from '../base-element';

export class DtButton extends BaseElement {
  static observedAttributes = ['variant', 'disabled', 'loading', 'type'];

  private _variant: 'primary' | 'secondary' | 'outline' | 'ghost' = 'primary';
  private _disabled = false;
  private _loading = false;
  private _type: 'button' | 'submit' = 'button';
  private _content = '';
  private _contentCaptured = false;

  get variant(): string {
    return this._variant;
  }

  set variant(value: string) {
    if (['primary', 'secondary', 'outline', 'ghost'].includes(value)) {
      this._variant = value as 'primary' | 'secondary' | 'outline' | 'ghost';
      this.updateRender();
    }
  }

  get disabled(): boolean {
    return this._disabled;
  }

  set disabled(value: boolean) {
    this._disabled = value;
    this.updateRender();
  }

  get loading(): boolean {
    return this._loading;
  }

  set loading(value: boolean) {
    this._loading = value;
    this.updateRender();
  }

  get type(): string {
    return this._type;
  }

  set type(value: string) {
    if (value === 'button' || value === 'submit') {
      this._type = value;
      this.updateRender();
    }
  }

  connectedCallback(): void {
    this.captureContent();
    this.updateRender();
    this.addEventListener('click', this.handleClick);
  }

  override disconnectedCallback(): void {
    super.disconnectedCallback();
    this.removeEventListener('click', this.handleClick);
  }

  attributeChangedCallback(name: string, _oldValue: string, newValue: string): void {
    switch (name) {
      case 'variant':
        this.variant = newValue;
        break;
      case 'disabled':
        this._disabled = newValue !== null && newValue !== 'false';
        this.updateRender();
        break;
      case 'loading':
        this._loading = newValue !== null && newValue !== 'false';
        this.updateRender();
        break;
      case 'type':
        this.type = newValue;
        break;
    }
  }

  private handleClick = (e: Event): void => {
    if (this._disabled || this._loading) {
      e.preventDefault();
      e.stopPropagation();
    }
  };

  private captureContent(): void {
    if (!this._contentCaptured) {
      this._content = this.innerHTML.trim();
      this._contentCaptured = true;
    }
  }

  private getVariantClasses(): string {
    const base =
      'inline-flex items-center justify-center rounded-lg h-12 px-5 text-base font-semibold leading-normal tracking-[0.015em] transition-all focus:outline-none focus:ring-4 active:scale-[0.98]';

    const variants: Record<string, string> = {
      primary:
        'bg-primary-500 text-white hover:bg-primary-600 focus:ring-primary-500/30 shadow-sm',
      secondary:
        'bg-primary-500/10 text-primary-600 hover:bg-primary-500/20 focus:ring-primary-500/10',
      outline:
        'bg-transparent border border-primary-500/30 text-primary-600 hover:bg-primary-500/5 focus:ring-primary-500/10',
      ghost:
        'bg-transparent text-primary-600 hover:bg-neutral-100 focus:ring-neutral-100',
    };

    return `${base} ${variants[this._variant]}`;
  }

  private getStateClasses(): string {
    if (this._disabled) {
      return 'cursor-not-allowed bg-neutral-100 text-neutral-400 hover:bg-neutral-100 border-transparent shadow-none';
    }
    if (this._loading) {
      return 'cursor-wait opacity-90 pointer-events-none';
    }
    return 'cursor-pointer';
  }

  private renderSpinner(): string {
    return `
      <svg class="animate-spin -ml-1 mr-3 h-4 w-4" fill="none" viewBox="0 0 24 24">
        <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
        <path class="opacity-75" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" fill="currentColor"></path>
      </svg>
    `;
  }

  private updateRender(): void {
    this.captureContent();
    const variantClasses = this.getVariantClasses();
    const stateClasses = this.getStateClasses();

    this.render(`
      <button 
        type="${this._type}"
        class="${variantClasses} ${stateClasses}"
        ${this._disabled ? 'disabled aria-disabled="true"' : ''}
        ${this._loading ? 'aria-busy="true"' : ''}
      >
        ${this._loading ? this.renderSpinner() : ''}
        <span class="truncate">${this._content}</span>
      </button>
    `);
  }
}

customElements.define('dt-button', DtButton);
